// frontend/src/components/shell/WorkspaceSwitcher.jsx
// The super admin's "Workspaces" section in the account menu and the way
// back from an entered workspace. Entering one (the Demo workspace, say) is
// an audited impersonation of that workspace's admin; returning restores
// the super admin's own session from their untouched refresh cookie. Both
// directions reload the page so every socket, cache and context starts
// fresh as the new identity.

import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import workspaceSwitchApi from "../../services/workspaceSwitchApi";
import Button from "../ui/Button";
import { BuildingIcon, CheckIcon } from "./icons";
import { HOME_PATH, useLeaveWorkspace, workspaceNav } from "./workspaceSwitch";

const isSuperAdmin = (user) => String(user?.role || "").toLowerCase() === "super_admin";

/** The persistent bar's action while inside another workspace. */
export const ReturnToWorkspaceButton = () => {
  const { leave, leaving } = useLeaveWorkspace();
  return (
    <Button variant="secondary" size="xs" isLoading={leaving} onClick={leave}>
      Back to my workspace
    </Button>
  );
};

/**
 * The slim bar above the shell. Inside an entered workspace it names the
 * workspace and carries the way back; in a demo tenant reached any other way
 * it is the sample-data notice. Layout measures it through the ref.
 */
export const WorkspaceBanner = React.forwardRef(({ user }, ref) => {
  const demo = Boolean(user?.demo);

  if (user?.impersonating) {
    return (
      <div
        ref={ref}
        role="region"
        aria-label="Workspace"
        className="sg-demo-banner"
        data-impersonating=""
      >
        <span>
          Viewing{" "}
          <strong className="font-medium text-sg-ink">
            {user.organizationName || "another"}
          </strong>{" "}
          workspace{demo && " — sample data; calling, email and imports are off"}
        </span>
        <ReturnToWorkspaceButton />
      </div>
    );
  }

  if (!demo) return null;

  return (
    <div ref={ref} role="status" className="sg-demo-banner">
      <span className="font-medium text-sg-ink">Demo workspace</span> — sample
      data. Calling, email, imports and account settings are disabled.
    </div>
  );
});

WorkspaceBanner.displayName = "WorkspaceBanner";

const WorkspaceRow = ({ workspace, busy, disabled, onEnter }) => (
  <button
    type="button"
    role="menuitemradio"
    aria-checked={Boolean(workspace.current)}
    aria-busy={busy || undefined}
    disabled={disabled}
    className="sg-menu-row"
    onClick={() => onEnter(workspace)}
  >
    <BuildingIcon size={15} className="sg-menu-icon" />
    <span className="min-w-0 flex-1 truncate">{workspace.name}</span>
    {busy ? (
      <span className="sg-menu-tag">Opening</span>
    ) : (
      workspace.isDemo && !workspace.current && <span className="sg-menu-tag">Sample data</span>
    )}
    {workspace.current && <CheckIcon size={14} className="sg-menu-check" />}
  </button>
);

/**
 * "Workspaces" in the account menu. A super admin sees their own workspace
 * and the demo workspaces; inside an entered workspace, anyone sees where
 * they are and the way back.
 */
export const WorkspaceMenuSection = ({ user, onClose }) => {
  const impersonating = Boolean(user?.impersonating);
  const canSwitch = isSuperAdmin(user) && !impersonating;
  const [state, setState] = useState({ status: canSwitch ? "loading" : "idle", rows: [] });
  const [entering, setEntering] = useState(null);
  const { leave, leaving } = useLeaveWorkspace();

  useEffect(() => {
    if (!canSwitch) return undefined;
    let active = true;
    workspaceSwitchApi
      .list()
      .then((rows) => active && setState({ status: "ready", rows }))
      .catch(() => active && setState({ status: "error", rows: [] }));
    return () => {
      active = false;
    };
  }, [canSwitch]);

  const enter = useCallback(
    async (workspace) => {
      if (workspace.current) {
        onClose?.();
        return;
      }
      setEntering(workspace.id);
      try {
        await workspaceSwitchApi.enter(workspace.id);
        workspaceNav.go(HOME_PATH);
      } catch (error) {
        setEntering(null);
        toast.error(error?.message || "That workspace could not be opened.");
      }
    },
    [onClose],
  );

  if (impersonating) {
    return (
      <div role="group" aria-label="Workspaces" className="sg-menu-section">
        <div className="sg-menu-head sg-micro">Workspaces</div>
        <WorkspaceRow
          workspace={{ id: user.organizationId, name: user.organizationName || "This workspace", current: true }}
          onEnter={() => onClose?.()}
        />
        <button
          type="button"
          role="menuitem"
          className="sg-menu-row"
          aria-busy={leaving || undefined}
          disabled={leaving}
          onClick={leave}
        >
          <span className="sg-menu-icon sg-menu-icon-spacer" aria-hidden="true" />
          <span className="flex-1">{leaving ? "Returning…" : "Back to my workspace"}</span>
        </button>
      </div>
    );
  }

  if (!canSwitch) return null;

  return (
    <div role="group" aria-label="Workspaces" className="sg-menu-section" aria-busy={state.status === "loading" || undefined}>
      <div className="sg-menu-head sg-micro">Workspaces</div>
      {state.status === "loading" && (
        <div className="sg-menu-row" aria-hidden="true">
          <span className="sg-skeleton sg-menu-skel" />
        </div>
      )}
      {state.status === "error" && (
        <p className="sg-menu-note" role="status">
          Workspaces could not be loaded.
        </p>
      )}
      {state.rows.map((workspace) => (
        <WorkspaceRow
          key={workspace.id}
          workspace={workspace}
          busy={entering === workspace.id}
          disabled={Boolean(entering)}
          onEnter={enter}
        />
      ))}
    </div>
  );
};

export default WorkspaceMenuSection;
